<!-- A settings list, not a wall of forms. Each row states the current value and opens its
     own form; the rest stay shut, so the page answers "what is my e-mail" without reading
     anything, and shows exactly one form when you act. Native <details>, so open state,
     keyboard and Escape are the browser's job.

     Two side-by-side cards were the wrong shape: they read as competing tasks and could
     never match in height, one form having two fields and the other three. -->
<div class="mx-auto max-w-2xl space-y-6">
  <h1 class="page-title text-2xl font-bold text-fg">Mijn gegevens</h1>

  <app-card>
    <ul role="list" class="-my-5 divide-y divide-border/60 sm:-my-6">
      <li>
        <details class="group py-5">
          <summary
            class="flex min-h-11 cursor-pointer list-none items-center justify-between gap-4 marker:content-none"
          >
            <span class="min-w-0">
              <span class="block text-sm text-fg-muted">E-mailadres</span>
              @if (user$ | async; as user) {
                <span class="block truncate font-semibold text-fg">{{
                  user.email
                }}</span>
              }
            </span>
            <span
              class="flex shrink-0 items-center gap-1 text-sm font-semibold text-brand"
            >
              Wijzigen
              <app-icon
                name="chevron-down"
                size="size-4"
                class="transition-transform group-open:rotate-180"
              />
            </span>
          </summary>

          <form
            [formGroup]="emailForm"
            (ngSubmit)="changeEmail()"
            class="mt-5 space-y-5"
          >
            <p class="max-w-prose text-sm text-fg-muted">
              Je nieuwe adres geldt pas nadat je de bevestigingslink opent.
            </p>

            <app-field
              label="Nieuw e-mailadres"
              for="newEmail"
              [control]="newEmail"
            >
              <input
                appInput
                [control]="newEmail"
                formControlName="newEmail"
                id="newEmail"
                type="email"
                autocomplete="email"
              />
            </app-field>

            <app-field
              label="Je huidige wachtwoord"
              for="emailPassword"
              [control]="emailPassword"
            >
              <input
                appInput
                [control]="emailPassword"
                formControlName="emailPassword"
                id="emailPassword"
                type="password"
                autocomplete="current-password"
              />
            </app-field>

            <button
              appButton
              type="submit"
              [disabled]="emailForm.invalid || emailSubmission"
            >
              Bevestigingslink versturen
            </button>

            @if (emailAlert.show) {
              <app-alert
                [severity]="emailAlert.severity"
                [message]="emailAlert.message"
              />
            }
          </form>
        </details>
      </li>

      <li>
        <details class="group py-5">
          <summary
            class="flex min-h-11 cursor-pointer list-none items-center justify-between gap-4 marker:content-none"
          >
            <span class="min-w-0">
              <span class="block text-sm text-fg-muted">Wachtwoord</span>
              <span class="block font-semibold tracking-widest text-fg"
                >••••••••</span
              >
            </span>
            <span
              class="flex shrink-0 items-center gap-1 text-sm font-semibold text-brand"
            >
              Wijzigen
              <app-icon
                name="chevron-down"
                size="size-4"
                class="transition-transform group-open:rotate-180"
              />
            </span>
          </summary>

          <form
            [formGroup]="passwordForm"
            (ngSubmit)="changePassword()"
            class="mt-5 space-y-5"
          >
            <app-field
              label="Je huidige wachtwoord"
              for="currentPassword"
              [control]="currentPassword"
            >
              <input
                appInput
                [control]="currentPassword"
                formControlName="currentPassword"
                id="currentPassword"
                type="password"
                autocomplete="current-password"
              />
            </app-field>

            <app-field
              label="Nieuw wachtwoord"
              for="newPassword"
              hint="Minstens 6 tekens."
              [control]="newPassword"
            >
              <input
                appInput
                [control]="newPassword"
                formControlName="newPassword"
                id="newPassword"
                type="password"
                autocomplete="new-password"
              />
            </app-field>

            <app-field
              label="Nieuw wachtwoord bevestigen"
              for="confirmPassword"
              [control]="confirmPassword"
            >
              <input
                appInput
                [control]="confirmPassword"
                formControlName="confirmPassword"
                id="confirmPassword"
                type="password"
                autocomplete="new-password"
              />
            </app-field>

            <button
              appButton
              type="submit"
              [disabled]="passwordForm.invalid || passwordSubmission"
            >
              Wachtwoord opslaan
            </button>

            @if (passwordAlert.show) {
              <app-alert
                [severity]="passwordAlert.severity"
                [message]="passwordAlert.message"
              />
            }
          </form>
        </details>
      </li>

      <li>
        <details class="group py-5">
          <summary
            class="flex min-h-11 cursor-pointer list-none items-center justify-between gap-4 marker:content-none"
          >
            <span class="min-w-0">
              <span class="block text-sm text-fg-muted">Account</span>
              <span class="block font-semibold text-danger-fg"
                >Mijn gegevens verwijderen</span
              >
            </span>
            <app-icon
              name="chevron-down"
              size="size-4"
              class="shrink-0 text-fg-muted transition-transform group-open:rotate-180"
            />
          </summary>

          <form
            [formGroup]="deleteForm"
            (ngSubmit)="confirmDelete()"
            class="mt-5 space-y-5"
          >
            <p class="max-w-prose text-sm text-fg">
              Dit verwijdert je account, je naam en e-mailadres, en al je
              reservaties. Fietsen die je nog had in komende lessen komen weer
              vrij.
              <strong class="font-semibold text-fg"
                >Dit kan niet ongedaan gemaakt worden.</strong
              >
            </p>

            <app-field
              label="Je huidige wachtwoord"
              for="deletePassword"
              [control]="deletePassword"
            >
              <input
                appInput
                [control]="deletePassword"
                formControlName="deletePassword"
                id="deletePassword"
                type="password"
                autocomplete="current-password"
              />
            </app-field>

            <app-field
              label="Typ VERWIJDER om te bevestigen"
              for="deleteConfirm"
              [control]="deleteConfirm"
            >
              <input
                appInput
                [control]="deleteConfirm"
                formControlName="deleteConfirm"
                id="deleteConfirm"
                placeholder="VERWIJDER"
              />
            </app-field>

            <button
              appButton
              variant="danger"
              type="submit"
              [disabled]="
                deleteForm.invalid || !deleteWordTyped || deleteSubmission
              "
            >
              Definitief verwijderen
            </button>

            @if (deleteAlert.show) {
              <app-alert
                [severity]="deleteAlert.severity"
                [message]="deleteAlert.message"
              />
            }
          </form>
        </details>
      </li>
    </ul>
  </app-card>
</div>
